// Shared presentational pieces. Everything renders with the class names defined in
// web/index.html — the design tokens live there, not in JS, so a colour is never invented
// in a component. Icons are Material Symbols ligatures (the mockup's icon set); the EN
// overlay skips `.ms` spans so an icon name is never "translated".

import type { ReactNode } from 'react';
import { initials, score10, scoreTone, toneVar, type Tone } from './format';

export function Icon({ name, className, style }: {
  name: string; className?: string; style?: React.CSSProperties;
}) {
  return (
    <span className={`ms${className ? ` ${className}` : ''}`} style={style} aria-hidden="true" translate="no">
      {name}
    </span>
  );
}

export type AvatarKind = 'accent' | 'neutral' | 'good' | 'bad';

export function Avatar({ name, kind, size }: {
  name: string; kind?: AvatarKind; size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  const cls = ['avatar'];
  if (size && size !== 'md') cls.push(size);
  if (kind && kind !== 'accent') cls.push(kind);
  return <span className={cls.join(' ')} translate="no">{initials(name)}</span>;
}

export function Pill({ tone, children, lg }: { tone: Tone | 'neu'; children: ReactNode; lg?: boolean }) {
  return <span className={`pill ${tone === 'neu' ? '' : tone}${lg ? ' lg' : ''}`.trim()}>{children}</span>;
}

export function ScorePill({ score, hasCritical, lg }: { score: number | null; hasCritical?: boolean; lg?: boolean }) {
  return (
    <Pill tone={scoreTone(score, hasCritical)} lg={lg}>
      {hasCritical ? '0,0' : score10(score)}
    </Pill>
  );
}

export function PageHead({ icon, title, subtitle, right }: {
  icon: string; title: ReactNode; subtitle?: ReactNode; right?: ReactNode;
}) {
  return (
    <div className="pagehead">
      <div className="pagehead-ic"><Icon name={icon} /></div>
      <div className="pagehead-txt">
        <h1>{title}</h1>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {right ? <div className="pagehead-right">{right}</div> : null}
    </div>
  );
}

export function Panel({ icon, title, hint, right, tone, children, footer, className }: {
  icon?: string; title?: ReactNode; hint?: ReactNode; right?: ReactNode; tone?: 'bad';
  children?: ReactNode; footer?: ReactNode; className?: string;
}) {
  return (
    <section className={`panel${className ? ` ${className}` : ''}`}>
      {(title || right) ? (
        <div className={`panel-hd${tone === 'bad' ? ' bad' : ''}`}>
          {icon ? <Icon name={icon} /> : null}
          <div className="panel-hd-txt">
            <div className="panel-title">{title}</div>
            {hint ? <div className="panel-sub">{hint}</div> : null}
          </div>
          {right}
        </div>
      ) : null}
      {children}
      {footer}
    </section>
  );
}

export function Kpi({ label, value, unit, icon, tone, delta, deltaKind }: {
  label: string; value: ReactNode; unit?: string; icon: string; tone: Tone;
  delta?: ReactNode; deltaKind?: 'up' | 'down' | 'flat';
}) {
  return (
    <div className="kpi">
      <span className={`kpi-ic tone-${tone}`}><Icon name={icon} /></span>
      <div className="kpi-body">
        <div className="kpi-val">{value}{unit ? <small>{unit}</small> : null}</div>
        <div className="kpi-lab">{label}</div>
        {delta ? (
          <div className={`kpi-delta${deltaKind === 'up' ? ' up' : deltaKind === 'down' ? ' down' : ''}`}>{delta}</div>
        ) : null}
      </div>
    </div>
  );
}

/** The mockup's "AĞIRLIKLI SKOR" block: big number, quality band, progress bar. */
/** Hero bar gridlines. 5/7/9 are the qualityLabel band edges, not decorative. */
const HERO_TICKS = [0, 5, 7, 9, 10];

export function ScoreHero({ score, hasCritical, label }: {
  score: number | null; hasCritical: boolean; label: string;
}) {
  const color = toneVar(scoreTone(score, hasCritical));
  const pct = hasCritical || score === null ? 0 : Math.max(0, Math.min(100, score * 10));
  return (
    <div className="hero">
      <div className="micro">Ağırlıklı skor</div>
      <div className="hero-val">
        <span className="hero-num" style={{ color }}>{hasCritical ? '0,0' : score10(score)}</span>
        <span className="hero-band" style={{ color }}>{label}</span>
      </div>
      <span className="bar"><i style={{ width: `${pct}%`, background: color }} /></span>
      {/* The labels are the quality bands (5 / 7 / 9), NOT even divisions — so each one is
          placed at its own value*10%, matching the fill. Spacing them evenly put "7" at the
          halfway mark and made a 6,3 bar look like it had cleared 7. */}
      <div className="hero-ticks">
        {HERO_TICKS.map((t) => (
          <span key={t} style={{ left: `${t * 10}%` }} translate="no">{t}</span>
        ))}
      </div>
    </div>
  );
}

/** One row of the sidebar breakdown: weight tile · name · bar · value. */
export function BreakdownRow({ label, weight, value }: { label: string; weight?: number; value: number | null }) {
  const tone = scoreTone(value);
  const color = toneVar(tone);
  const pct = value === null ? 0 : value * 10;
  return (
    <div className="brk-row">
      <span className="code">{weight !== undefined ? `%${weight}` : ''}</span>
      <span className="nm">{label}</span>
      <span className="bar thin"><i style={{ width: `${pct}%`, background: value === null ? 'transparent' : color }} /></span>
      <span className="val" style={{ color }}>{score10(value)}</span>
    </div>
  );
}

/** Dashboard bar: name (+ weight) · bar · value. */
export function CriterionBar({ label, value, weight }: {
  label: string; value: number | null; weight?: number;
}) {
  const color = toneVar(scoreTone(value));
  const pct = value === null ? 0 : (value / 10) * 100;
  return (
    <div className="barrow">
      <span className="nm">
        {label}
        {weight !== undefined ? <small> · %{weight}</small> : null}
      </span>
      <span className="bar"><i style={{ width: `${pct}%`, background: value === null ? 'transparent' : color }} /></span>
      <span className="val" style={{ color }}>{score10(value)}</span>
    </div>
  );
}

export function Callout({ tone, icon, children, center }: {
  tone?: 'accent' | 'bad' | 'warn' | 'good' | 'neu'; icon?: string; children: ReactNode; center?: boolean;
}) {
  const t = tone ?? 'neu';
  const ic = icon ?? (t === 'bad' ? 'error' : t === 'warn' ? 'priority_high' : t === 'good' ? 'check_circle' : 'info');
  return (
    <div className={`callout${t === 'neu' ? '' : ` ${t}`}${center ? ' center' : ''}`}>
      <Icon name={ic} />
      <span>{children}</span>
    </div>
  );
}

/** Kept for the pages that only need "info or error" — a thin wrapper over Callout. */
export function Note({ children, error }: { children: ReactNode; error?: boolean }) {
  return <Callout tone={error ? 'bad' : 'accent'}>{children}</Callout>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Loading() {
  return <div className="empty">Yükleniyor…</div>;
}

export function CriticalBanner({ names }: { names: string[] }) {
  return (
    <div className="critbanner">
      <Icon name="warning" />
      <div>
        <div className="t">Kritik hata · çağrı otomatik 0,0 aldı</div>
        {/* One span per rule: the EN overlay matches whole text nodes, and a joined string
            would never equal a dictionary key. */}
        <div className="d">
          {names.map((n, i) => (
            <span key={n}>{i > 0 ? <span> · </span> : null}<span>{n}</span></span>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Score selector: 0–10 option buttons, coloured by the band the value lands in. */
export function ScorePicker({ value, onChange, disabled }: {
  value: number | null; onChange: (n: number) => void; disabled?: boolean;
}) {
  return (
    <div className="opts">
      {Array.from({ length: 11 }, (_, n) => {
        const sel = value === n;
        return (
          <button
            key={n}
            type="button"
            disabled={disabled}
            className={`opt${sel ? ` sel ${scoreTone(n)}` : ''}`}
            aria-pressed={sel}
            onClick={() => onChange(n)}
          >
            {n}
          </button>
        );
      })}
    </div>
  );
}

/** A custom checkbox in the mockup's style (16px box, accent/red when checked). */
export function Check({ checked, onChange, children, crit }: {
  checked: boolean; onChange: (on: boolean) => void; children: ReactNode; crit?: boolean;
}) {
  return (
    <label className={`chk${crit ? ' crit' : ''}`}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="box"><Icon name="check" /></span>
      <span className="grow">{children}</span>
    </label>
  );
}

export function CodeTile({ children, kind }: { children: ReactNode; kind?: 'neu' | 'bad' }) {
  return <span className={`code-tile${kind ? ` ${kind}` : ''}`}>{children}</span>;
}

/**
 * The topbar's global brand filter (owner, 2026-09-06 — "as it is on the admin panel").
 *
 * Single-select with an explicit "Tümü" rather than call-agent-admin's multi-select chips:
 * the API's `?brand=` takes ONE key, and with two brands the three states here already
 * cover every combination that panel's toggles can express. Rendered only when the caller
 * can see more than one brand (`meta.brands` is already scoped by the server), so a
 * brand-locked supervisor or QMS specialist never sees a filter with one option — and the
 * server re-checks every request anyway (canSeeBrand → 403). Hiding a control is not a
 * permission.
 */
export function BrandFilter({ brands, value, onSet }: {
  brands: { key: string; name: string }[];
  value: string;
  onSet: (b: string) => void;
}) {
  if (brands.length < 2) return null;
  return (
    <div className="brandbar">
      <Icon name="filter_alt" />
      {[{ key: '', name: 'Tümü' }, ...brands].map((b) => (
        <button
          key={b.key || 'all'}
          type="button"
          className={value === b.key ? 'on' : ''}
          onClick={() => onSet(b.key)}
        >
          <span className="dot" />
          <span>{b.name}</span>
        </button>
      ))}
    </div>
  );
}

export function LangSwitch({ lang, onSet }: { lang: 'tr' | 'en'; onSet: (l: 'tr' | 'en') => void }) {
  return (
    <div className="lang-switch" translate="no">
      {(['tr', 'en'] as const).map((l) => (
        <button key={l} type="button" className={lang === l ? 'on' : ''} onClick={() => onSet(l)}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
